import { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Plus, Search, Pen, Trash2, X as IconX, Upload, Boxes, Grid3x3, ChevronLeft, ChevronRight, ArrowLeft, Barcode, Image as ImageIcon, Zap, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { VariantBuilder } from '../../components/VariantBuilder';
import { CustomSelect } from '../../components/CustomSelect';
import { ImageCarousel } from '../../components/ImageCarousel';
import { useSharedStore } from '../../hooks/useSharedStore';
import {
  catalogProductsOf, pSubCategoriesOf, pCategoriesOf, gstRatesOf, shopTaxMode,
  addCatalogProduct, updateCatalogProduct, deleteCatalogProduct,
  buildVariantMatrix, comboLabel, comboKey, findGst, findPSubCategory, findPCategory,
  stockRowsOf, addStockRow, isModuleOn, branchesOf, updateStockRow,
} from '../../data/store';
import { formatCategoryName, formatNumber } from '../../utils/formatters';
import { IconAction, RowActions } from '../../components/RowActions';
import { ViewToggle } from '../../components/ViewToggle';

let variantSeq = 0;

// How product's images relate to its variants.
const IMAGE_MODES = [
  { value: 'common', label: 'Common images' },
  { value: 'variant', label: 'Variant images' },
  { value: 'both', label: 'Both' },
];

// Generates next guaranteed-unique store barcode (sequential 6-digit GS1 internal range 200001+)
export const generateUniqueStoreBarcode = (orgId, currentDraft = null) => {
  const allProds = catalogProductsOf(orgId) || [];
  const existingCodes = new Set();

  allProds.forEach((p) => {
    if (p.barcode) existingCodes.add(String(p.barcode).trim());
    if (Array.isArray(p.variants)) {
      p.variants.forEach((v) => {
        if (v.barcode) existingCodes.add(String(v.barcode).trim());
      });
    }
  });

  if (currentDraft) {
    if (currentDraft.barcode) existingCodes.add(String(currentDraft.barcode).trim());
    if (Array.isArray(currentDraft.variants)) {
      currentDraft.variants.forEach((v) => {
        if (v.barcode) existingCodes.add(String(v.barcode).trim());
      });
    }
  }

  let maxCode = 200000;
  existingCodes.forEach((code) => {
    const num = parseInt(code, 10);
    if (!isNaN(num) && num >= 200000 && num < 999999) {
      if (num > maxCode) maxCode = num;
    }
  });

  let candidate = maxCode + 1;
  while (existingCodes.has(String(candidate))) {
    candidate++;
  }
  return String(candidate);
};

const blank = (orgId) => ({
  id: null,
  orgId,
  subCategoryId: '',
  name: '',
  description: '',
  images: [],
  imageMode: 'common',
  gstId: '',
  // Empty means follow the shop, which is what almost every product does.
  taxMode: '',
  basePrice: 0,
  stockQty: 0,
  variantFields: [],
  variants: [],
  barcode: '',
  shelfLifeDays: '',
});

// Read picked files, compress them, and return data-URL strings.
const readFiles = (files) => Promise.all(
  files.map((file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX_WIDTH = 800;
        const MAX_HEIGHT = 800;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height = Math.round(height * (MAX_WIDTH / width));
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width = Math.round(width * (MAX_HEIGHT / height));
            height = MAX_HEIGHT;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Use webp for smaller file size and good quality, fallback to original if very small
        resolve(canvas.toDataURL('image/webp', 0.8));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  })),
);

// A horizontal strip of image thumbnails plus an upload tile. `onChange`
// receives the full next image array (after an add or a remove). No cap.
const ImageStrip = ({ images, onChange }) => (
  <div className="flex items-center gap-3 overflow-x-auto custom-scrollbar pb-1">
    {images.map((img, idx) => (
      <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-[var(--theme-tertiary)] shrink-0 group">
        <img src={img} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover" />
        <button
          type="button"
          onClick={() => onChange(images.filter((_, i) => i !== idx))}
          className="absolute top-1 right-1 p-1 bg-black/50 hover:bg-rose-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <IconX size={10} />
        </button>
      </div>
    ))}
    <label className="w-20 h-20 rounded-lg border border-dashed border-[var(--theme-tertiary)] flex flex-col items-center justify-center gap-1 hover:border-[var(--theme-primary)] hover:bg-[var(--theme-primary-soft)] hover:text-[var(--theme-primary)] text-slate-400 shrink-0 transition-colors cursor-pointer">
      <Upload size={16} aria-hidden="true" />
      <span className="text-[11px] font-semibold uppercase tracking-[0.1em]">Upload</span>
      <input
        type="file" accept="image/*" multiple className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          e.target.value = null;
          if (files.length) readFiles(files).then((imgs) => onChange([...images, ...imgs]));
        }}
      />
    </label>
  </div>
);

ImageStrip.propTypes = {
  images: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

// Rebuilds the variant rows from the field definitions, carrying over prices
// and per-variant images for combinations that still exist.
const syncVariants = (variantFields, basePrice, existing) => {
  const priceByKey = {};
  const imagesByKey = {};
  const stockByKey = {};
  const barcodeByKey = {};
  (existing || []).forEach((v) => {
    priceByKey[comboKey(v.combo)] = v.price;
    imagesByKey[comboKey(v.combo)] = v.images || [];
    stockByKey[comboKey(v.combo)] = v.stockQty;
    barcodeByKey[comboKey(v.combo)] = v.barcode || '';
  });
  const combos = buildVariantMatrix(variantFields);
  return combos.map((combo) => {
    const key = comboKey(combo);
    return {
      id: `v-${++variantSeq}`,
      combo,
      label: comboLabel(combo),
      price: priceByKey[key] !== undefined ? priceByKey[key] : Number(basePrice) || 0,
      stockQty: stockByKey[key] !== undefined ? stockByKey[key] : 0,
      images: imagesByKey[key] || [],
      barcode: barcodeByKey[key] || '',
    };
  });
};

const ProductForm = ({
  org,
  open,
  draft,
  setDraft,
  onClose,
  onSubmit,
  mode,
  subCats,
  cats,
  gstList,
  accent,
  showNotification
}) => {
  // The rate this product will be billed at: its shelf's where there is one,
  // and otherwise whatever the product already carries.
  //
  // Every P-Sub-Category in an existing shop has no rate on it yet, so
  // falling back to the product's own is what keeps opening a product to fix
  // its name from saving away the rate it has been sold at for a year.
  const shelfGst = subCats.find((x) => x.id === draft.subCategoryId)?.gstId || '';
  const shownGst = gstList.find((g) => g.id === (shelfGst || draft.gstId));
  // Whether there is any tax to talk about. A rate of 0% is a rate a shop can
  // choose, and it is still nothing to be inside or on top of a price.
  const taxed = Number(shownGst?.rate) > 0;

  // What the picker below starts on when the product has never stated a
  // mode - which is every product saved while "Follow the shop" was an
  // option, and it was the default.
  //
  // The server still reads an empty column as the shop's own setting, so
  // that is what is shown. Starting on "Exclusive" would have shown an
  // inclusive shop that word over a product billed inclusive since the day
  // it was added, and saving the form would have made it true.
  const taxNow = draft.taxMode || shopTaxMode();

  const barcodeMode = org?.barcodeMode || 'both';
  const barcodeLabel = barcodeMode === 'product'
    ? 'Factory EAN / Barcode'
    : barcodeMode === 'sticky'
    ? 'Store Sticky Barcode'
    : 'Barcode (EAN / Sticky)';

  const barcodePlaceholder = barcodeMode === 'product'
    ? 'Scan or type manufacturer barcode'
    : barcodeMode === 'sticky'
    ? 'Click Auto-Generate or type code'
    : 'Scan factory EAN or click Auto-Generate';

  const canAutoGenerate = barcodeMode === 'sticky' || barcodeMode === 'both';

  // No early return above the hooks: React requires every hook to run on every
  // render, in the same order. A `return null` here ran zero hooks while closed
  // and three while open, which throws "Rendered more hooks than during the
  // previous render" as the modal opens. The guard below, after the hooks, is
  // the correct one and was already present — this line was a leftover.
  const [errors, setErrors] = useState({});
  // Live matrix preview synced to the current field definitions.
  // Computed unconditionally — hooks must run on every render (Rules of Hooks).
  const previewVariants = useMemo(
    () => syncVariants(draft.variantFields, draft.basePrice, draft.variants),
    [draft.variantFields, draft.basePrice, draft.variants],
  );

  useEffect(() => {
    if (!open) setErrors({});
  }, [open]);

  if (!open) return null;

  // The image-mode radio is always available; the per-variant uploaders simply
  // wait until the product actually has variant combinations.
  const hasVariants = previewVariants.length > 0;
  const imageMode = draft.imageMode || 'common';
  const showCommon = imageMode === 'common' || imageMode === 'both';
  const showVariant = imageMode === 'variant' || imageMode === 'both';

  const setVariantPrice = (key, price) => {
    setDraft((d) => ({
      ...d,
      variants: previewVariants.map((v) => ({
        ...v,
        price: comboKey(v.combo) === key ? (Number(price) || 0) : v.price,
      })),
    }));
  };

  const setVariantStock = (key, stockQty) => {
    setDraft((d) => ({
      ...d,
      variants: previewVariants.map((v) => ({
        ...v,
        stockQty: comboKey(v.combo) === key ? (Number(stockQty) || 0) : v.stockQty,
      })),
    }));
  };

  const setVariantBarcode = (key, barcode) => {
    setDraft((d) => ({
      ...d,
      variants: previewVariants.map((v) => ({
        ...v,
        barcode: comboKey(v.combo) === key ? barcode : (v.barcode || ''),
      })),
    }));
  };

  const setVariantImages = (key, images) => {
    setDraft((d) => ({
      ...d,
      variants: previewVariants.map((v) => ({
        ...v,
        images: comboKey(v.combo) === key ? images : (v.images || []),
      })),
    }));
  };

  const handleSubmit = () => {
    const newErrors = {};
    if (!draft.subCategoryId) newErrors.subCategoryId = 'Please select a P-Sub-Category.';
    if (!draft.name || !draft.name.trim()) newErrors.name = 'Please fill the Product name.';
    // No GST guard. A P-Sub-Category may be saved without a rate, so a
    // product under one has nothing to inherit, and refusing the save here
    // would strand it on a form where GST is no longer a field to fix.
    if (!hasVariants && (draft.basePrice === '' || isNaN(draft.basePrice) || Number(draft.basePrice) < 0)) {
      newErrors.basePrice = 'Please provide a valid Price >= 0.';
    }


    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (showNotification) showNotification('Please fix the highlighted fields.', 'error');
      return;
    }
    setErrors({});

    const finalMode = hasVariants ? imageMode : 'common';
    const images = draft.images || [];
    const variants = previewVariants;
    const stockQty = Number(draft.stockQty) || 0;
    // The mode goes with it even where the form only displayed it. A product
    // that has never stated one is shown the shop's, and saving is what
    // writes that down - so the next time the shop-wide setting changes,
    // this product keeps being billed the way it is billed today.
    onSubmit({
      ...draft, taxMode: taxNow, imageMode: finalMode, images, variants, stockQty,
    });
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      className="w-full h-full flex flex-col flex-1 bg-white rounded-lg shadow-sm border border-[var(--theme-tertiary)] overflow-hidden"
    >
          <header className="px-6 py-4 border-b border-[var(--theme-tertiary)] flex items-center justify-between shrink-0 bg-slate-50">
            <div className="flex items-center gap-3">
              <button type="button" onClick={onClose} aria-label="Go back" className="w-8 h-8 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors shadow-sm">
                <ArrowLeft size={16} />
              </button>
              <div>
                <h3 className="text-lg font-extrabold text-[#0d0d0d]">{mode === 'edit' ? 'Edit product' : 'New product'}</h3>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400 mt-1">P-Category → P-Sub-Category → Product</p>
              </div>
            </div>
          </header>

          <div className="px-6 py-5 overflow-y-auto custom-scrollbar flex flex-col gap-4">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Images</span>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                  {IMAGE_MODES.map((m) => {
                    const selected = imageMode === m.value;
                    return (
                      <label key={m.value} className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio" name="imageMode" className="sr-only" checked={selected}
                          onChange={() => setDraft({ ...draft, imageMode: m.value })}
                        />
                        <span className="w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors"
                          style={{ borderColor: selected ? accent : '#cbd5e1' }} aria-hidden="true">
                          {selected && <span className="w-2 h-2 rounded-full" style={{ background: accent }} />}
                        </span>
                        <span className="text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: selected ? accent : '#64748b' }}>
                          {m.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {showCommon && (
                <div className="flex flex-col gap-1.5">
                  {imageMode === 'both' && (
                    <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Common images</span>
                  )}
                  <ImageStrip images={draft.images || []} onChange={(imgs) => setDraft((d) => ({ ...d, images: imgs }))} />
                </div>
              )}

              {showVariant && (
                <div className="flex flex-col gap-3 rounded-lg border border-[var(--theme-tertiary)] p-3" style={{ background: 'var(--theme-secondary)' }}>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Variant images</span>
                  {hasVariants ? (
                    previewVariants.map((v) => (
                      <div key={comboKey(v.combo)} className="flex flex-col gap-1.5">
                        <span className="text-[11px] font-bold text-[#0d0d0d]">{v.label}</span>
                        <ImageStrip images={v.images || []} onChange={(imgs) => setVariantImages(comboKey(v.combo), imgs)} />
                      </div>
                    ))
                  ) : (
                    <p className="text-[11px] font-medium text-slate-400">Add variant fields below — each variant combination will get its own image uploader here.</p>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block space-y-1 md:col-span-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  P-Sub-Category <span className="text-rose-500 ml-1">*</span>
                </span>
                <CustomSelect
                  value={draft.subCategoryId}
                  onChange={(v) => {
                    // The shelf is how the rate and tax mode are chosen now, so they travel
                    // with it - but only when the shelf has them to give.
                    const chosen = subCats.find((x) => x.id === v);
                    const shelf = chosen?.gstId || '';
                    const shelfMode = chosen?.taxMode || '';
                    setDraft({
                      ...draft,
                      subCategoryId: v,
                      ...(shelf ? { gstId: shelf } : {}),
                      ...(shelfMode ? { taxMode: shelfMode } : {}),
                    });
                    if (errors.subCategoryId) setErrors({ ...errors, subCategoryId: undefined });
                  }}
                  placeholder="— Select P-Sub-Category —"
                  options={subCats.map((sc) => {
                    const cat = cats.find((c) => c.id === sc.categoryId);
                    return { value: sc.id, label: `${cat ? `${cat.name} › ` : ''}${sc.name}` };
                  })}
                  className={`w-full rounded-lg bg-gray-50 border ${errors.subCategoryId ? 'border-rose-400' : 'border-gray-100'} normal-case text-[#0d0d0d]`}
                />
                {errors.subCategoryId && <p className="text-[11px] font-medium text-rose-500 mt-1">{errors.subCategoryId}</p>}
              </label>
              <label className={`block space-y-1 ${!hasVariants ? 'md:col-span-1' : 'md:col-span-2'}`}>
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Product name <span className="text-rose-500 ml-1">*</span>
                </span>
                <input type="text" value={draft.name} onChange={(e) => {
                  setDraft({ ...draft, name: formatCategoryName(e.target.value) });
                  if (errors.name) setErrors({ ...errors, name: undefined });
                }} placeholder="e.g. Carrot"
                  className={`w-full rounded-lg bg-gray-50 border ${errors.name ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]`} />
                {errors.name && <p className="text-[11px] font-medium text-rose-500 mt-1">{errors.name}</p>}
              </label>

              {!hasVariants && (
                <div className="block space-y-1 md:col-span-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                      {barcodeLabel}
                    </span>
                    {canAutoGenerate && (
                      <button
                        type="button"
                        onClick={() => {
                          const code = generateUniqueStoreBarcode(org?.id, draft);
                          setDraft({ ...draft, barcode: code });
                          if (showNotification) showNotification(`Generated unique barcode: ${code}`, 'success');
                        }}
                        className="text-[10px] font-bold text-amber-700 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 px-2 py-0.5 rounded border border-amber-200 inline-flex items-center gap-1 transition-colors"
                        title="Auto-generate next unique store barcode"
                      >
                        <Zap size={11} /> Auto-Generate
                      </button>
                    )}
                  </div>
                  <div className="relative flex items-center">
                    <Barcode className="absolute left-2.5 text-slate-400 pointer-events-none" size={14} />
                    <input
                      type="text"
                      value={draft.barcode || ''}
                      onChange={(e) => {
                        setDraft({ ...draft, barcode: e.target.value });
                      }}
                      placeholder={barcodePlaceholder}
                      className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:border-[var(--theme-primary)] focus:bg-white outline-none pl-8 pr-3 py-2 text-xs font-mono font-medium text-[#0d0d0d]"
                    />
                  </div>
                </div>
              )}
              <label className="block space-y-1 md:col-span-1">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                    Shelf Life (Days)
                  </span>
                  <span className="text-[10px] text-slate-400 font-medium">Optional (Fresh Items)</span>
                </div>
                <div className="relative flex items-center">
                  <Calendar className="absolute left-2.5 text-slate-400 pointer-events-none" size={14} />
                  <input
                    type="number"
                    min="0"
                    value={draft.shelfLifeDays !== undefined ? draft.shelfLifeDays : ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10) || 0);
                      setDraft({ ...draft, shelfLifeDays: val });
                    }}
                    placeholder="e.g. 5 (Tomato), 30 (Potato)"
                    className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:border-[var(--theme-primary)] focus:bg-white outline-none pl-8 pr-3 py-2 text-xs font-medium text-[#0d0d0d]"
                  />
                </div>
              </label>
              <label className="block space-y-1 md:col-span-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Description</span>
                <textarea rows={2} value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  className="w-full rounded-lg bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] resize-none" />
              </label>
              {/* Shown, not asked.

                  It was a box on this form, which asked a shop to classify
                  rice as 5% once per packet of rice. The same decision made
                  a thousand times is a decision made differently a few of
                  those times, and the few are found when the return is
                  filed. It is chosen once, on the P-Sub-Category.

                  The product still stores its own gstId and the bill still
                  reads it - this only decides what goes in. Changing a
                  shelf's rate therefore does not rewrite the products
                  already under it, which is the right way round: a bill
                  printed last March was printed at last March's rate. */}
              <label className={`block space-y-1 ${hasVariants ? 'md:col-span-2' : ''}`}>
                {/* The note under the label carries the instruction, so the
                    box itself can just say what the rate is. "None on this
                    P-Sub-Category" put a whole sentence where a figure goes
                    and read like an error. */}
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  GST <span className="normal-case tracking-normal font-medium text-slate-400 ml-1">from the P-Sub-Category</span>
                </span>
                <div className={`w-full rounded-lg bg-slate-100 border border-gray-100 px-3 py-2 text-xs font-medium ${shownGst ? 'text-slate-600' : 'text-slate-400'}`}>
                  {shownGst
                    ? `${shownGst.name} (${shownGst.rate}%)`
                    : (draft.subCategoryId ? 'No GST' : '–')}
                </div>
              </label>
              {/* Whether this price already contains its GST. Two answers,
                  and both of them are about this product.

                  There was a third, "Follow the shop", and it was the
                  default. It read as the safe answer and it is the one
                  nobody at this form could check: what the product would
                  actually be billed at lived behind a setting on the GST
                  tab. A shop is rarely all one thing anyway - packaged
                  goods at a printed MRP beside loose produce the shop
                  prices itself - so the shop-wide answer was the wrong one
                  for half the list either way. */}
              {/* Answerable only when there is tax to answer about. Inclusive
                  and exclusive are two ways of saying where the GST sits in a
                  price, and on a product with no GST they are the same number.
                  The saved value is left alone, so a product that had one keeps
                  it if a rate comes back. */}
              <label className="block space-y-1">
                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  GST on the price
                  {!taxed && <span className="normal-case tracking-normal font-medium text-slate-400 ml-1">nothing to include without GST</span>}
                </span>
                {taxed ? (
                  <CustomSelect
                    value={taxNow}
                    onChange={(v) => setDraft({ ...draft, taxMode: v })}
                    placeholder="— Select —"
                    options={[
                      { value: 'exclusive', label: 'Exclusive GST - the tax goes on top' },
                      { value: 'inclusive', label: 'Inclusive GST - an MRP, tax already inside' },
                    ]}
                    className="w-full rounded-lg bg-gray-50 border border-gray-100 normal-case text-[#0d0d0d]"
                  />
                ) : (
                  <div className="w-full rounded-lg bg-slate-100 border border-gray-100 px-3 py-2 text-xs font-medium text-slate-400">
                    –
                  </div>
                )}
              </label>
              {!hasVariants && (
                <>
                  <label className="block space-y-1">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Price (₹)</span>
                    <input type="text" value={draft.basePrice}
                      onChange={(e) => {
                        setDraft({ ...draft, basePrice: formatNumber(e.target.value) });
                        if (errors.basePrice) setErrors({ ...errors, basePrice: undefined });
                      }}
                      className={`w-full rounded-lg bg-gray-50 border ${errors.basePrice ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d]`} />
                    {errors.basePrice && <p className="text-[11px] font-medium text-rose-500 mt-1">{errors.basePrice}</p>}
                  </label>
                </>
              )}
            </div>

            <div className="rounded-lg border border-[var(--theme-tertiary)] overflow-hidden flex flex-col shadow-[0_2px_12px_-4px_rgba(0,0,0,0.05)] shrink-0">
              <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-[var(--theme-tertiary)]">
                {/* Variant matrix builder */}
                <VariantBuilder
                  fields={draft.variantFields}
                  onChange={(variantFields) => setDraft({ ...draft, variantFields })}
                  accent={accent}
                />

                {/* Matrix preview */}
                <div className="flex flex-col bg-slate-50/50">
                  <div className="h-[53px] px-5 flex items-center justify-between shrink-0 border-b border-[var(--theme-tertiary)] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.02)] relative z-10">
                    <p className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-slate-600">
                      Variant matrix
                    </p>
                    <div className="flex items-center gap-2">
                      {canAutoGenerate && previewVariants.length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            let curr = { ...draft };
                            const updatedVars = (draft.variants || []).map((v) => {
                              if (!v.barcode) {
                                const code = generateUniqueStoreBarcode(org?.id, curr);
                                const updated = { ...v, barcode: code };
                                curr = { ...curr, variants: (curr.variants || []).map(x => x.id === v.id ? updated : x) };
                                return updated;
                              }
                              return v;
                            });
                            setDraft({ ...draft, variants: updatedVars });
                            if (showNotification) showNotification('Auto-filled missing variant barcodes', 'success');
                          }}
                          className="text-[9px] font-bold text-amber-700 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded border border-amber-200 inline-flex items-center gap-1 transition-colors"
                          title="Auto-generate barcodes for any variants without barcode"
                        >
                          <Zap size={10} /> Auto-Fill All
                        </button>
                      )}
                      <span 
                        className="text-[10px] font-extrabold uppercase tracking-[0.14em]"
                        style={{ color: accent }}
                      >
                        {previewVariants.length || 1} row{(previewVariants.length || 1) === 1 ? '' : 's'}
                      </span>
                    </div>
                  </div>
                <div className="max-h-64 lg:max-h-[300px] overflow-y-auto custom-scrollbar p-3">
                  {previewVariants.length === 0 ? (
                    <div className="px-4 py-3 flex items-center justify-between bg-slate-50 border border-gray-100 rounded-lg">
                      <span className="text-[11px] font-medium text-slate-500">Single variant — the product itself</span>
                      <span className="text-[11px] font-bold text-[#0d0d0d]">₹{(Number(draft.basePrice) || 0).toFixed(2)}</span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-2">
                      {previewVariants.map((v) => (
                        <div key={comboKey(v.combo)} className="px-3 py-2 flex items-center justify-between border border-[var(--theme-tertiary)] rounded-lg bg-white shadow-[0_2px_8px_-4px_rgba(0,0,0,0.05)]">
                          <span className="text-[11px] font-bold text-[#0d0d0d] truncate mr-2" title={v.label}>{v.label}</span>
                          <div className="flex items-center gap-2 shrink-0">
                            <div className="relative flex items-center">
                              <Barcode className="absolute left-2 text-slate-400 pointer-events-none" size={13} strokeWidth={2.5} />
                              <input
                                type="text" value={v.barcode || ''} placeholder="Barcode"
                                onChange={(e) => setVariantBarcode(comboKey(v.combo), e.target.value)}
                                className={`w-32 rounded-md bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none pl-7 ${canAutoGenerate ? 'pr-6' : 'pr-2'} py-1.5 text-[11px] font-mono font-medium text-[#0d0d0d] transition-colors`}
                              />
                              {canAutoGenerate && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const code = generateUniqueStoreBarcode(org?.id, draft);
                                    setVariantBarcode(comboKey(v.combo), code);
                                  }}
                                  className="absolute right-1 text-amber-600 hover:text-amber-800 p-0.5 rounded hover:bg-amber-50 text-[10px] font-bold"
                                  title="Auto-generate barcode for this variant"
                                >
                                  ⚡
                                </button>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <span className="text-[11px] font-semibold text-slate-400">₹</span>
                              <input
                                type="text" value={v.price}
                                onChange={(e) => setVariantPrice(comboKey(v.combo), formatNumber(e.target.value))}
                                className="w-20 rounded-md bg-gray-50 border border-gray-100 focus:bg-white focus:border-[var(--theme-primary)] outline-none px-2 py-1 text-[11px] font-medium text-[#0d0d0d] text-right transition-colors"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            </div>
          </div>

          <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-end gap-3 shrink-0 bg-slate-50">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:text-slate-700">Cancel</button>
            <button
              type="button"
              onClick={handleSubmit}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] transition-colors"
              style={{ background: accent }}
            >
              {mode === 'edit' ? 'Save product' : 'Create product'}
            </button>
          </footer>
    </motion.section>
  );
};

ProductForm.propTypes = {
  org: PropTypes.object,
  locations: PropTypes.array,
  open: PropTypes.bool.isRequired,
  draft: PropTypes.object.isRequired,
  setDraft: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  subCats: PropTypes.array.isRequired,
  cats: PropTypes.array.isRequired,
  gstList: PropTypes.array.isRequired,
  accent: PropTypes.string.isRequired,
  showNotification: PropTypes.func,
};

const ProductCard = ({ org, p, sub, cat, gst, stock, inventoryRows = [], accent, onEdit, onRemove }) => {
  const hasMultipleVariants = p.variants && p.variants.length > 1;
  const [activeVariantId, setActiveVariantId] = useState(() => {
    return hasMultipleVariants ? p.variants[0].id : null;
  });

  useEffect(() => {
    if (hasMultipleVariants) {
      if (!activeVariantId || !p.variants.some(v => v.id === activeVariantId)) {
        setActiveVariantId(p.variants[0].id);
      }
    } else {
      if (activeVariantId !== null) setActiveVariantId(null);
    }
  }, [p.variants, hasMultipleVariants, activeVariantId]);

  let currentStock = stock || 0;
  if (hasMultipleVariants && activeVariantId) {
    if (org && !isModuleOn(org, 'inventory')) {
      const v = p.variants.find(v => v.id === activeVariantId);
      if (v && v.stockQty !== undefined) currentStock = Number(v.stockQty) || 0;
    } else {
      const v = p.variants.find(v => v.id === activeVariantId);
      const vId = v?.id === 'base' ? '' : (v?.id || '');
      currentStock = inventoryRows.filter(r => r.productId === p.id && (r.variantId || '') === vId).reduce((sum, r) => sum + r.qty, 0);
    }
  }

  // Calculate the images to show
  let displayImages = [];
  const imageMode = p.imageMode || 'common';
  
  if (hasMultipleVariants && activeVariantId) {
    const v = p.variants.find(v => v.id === activeVariantId);
    const vImgs = v && v.images ? v.images : [];
    const cImgs = p.images ? p.images : [];
    
    if (imageMode === 'variant') {
      displayImages = vImgs.length > 0 ? vImgs : cImgs;
    } else if (imageMode === 'both') {
      // Avoid duplicating the variant image if it's already in common images
      const uniqueCImgs = cImgs.filter(img => !vImgs.includes(img));
      displayImages = [...vImgs, ...uniqueCImgs];
    } else {
      displayImages = cImgs.length > 0 ? cImgs : vImgs;
    }
  } else {
    displayImages = (p.images && p.images.length > 0)
      ? p.images
      : (p.variants || []).flatMap((v) => v.images || []);
  }

  // Deduplicate just in case
  displayImages = Array.from(new Set(displayImages));

  const variantCount = p.variants?.length || 0;

  return (
    <motion.article initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="h-full bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden flex flex-col hover:border-[var(--theme-primary-tint)] transition-colors">
      <div className="aspect-[4/3] w-full overflow-hidden relative shrink-0" style={{ background: 'var(--theme-secondary)' }}>
        {displayImages.length > 0
          ? <ImageCarousel images={displayImages} alt={p.name} />
          : <div className="w-full h-full flex items-center justify-center text-slate-300"><Boxes size={34} aria-hidden="true" /></div>}
      </div>
      <div className="p-3.5 flex flex-col gap-2 flex-1 min-w-0">
        {hasMultipleVariants && (
          <div className="flex items-center gap-1.5 overflow-x-auto custom-scrollbar pb-1 -mt-0.5">
            {p.variants.map(v => {
              const thumb = v.images?.[0] || p.images?.[0];
              const isActive = activeVariantId === v.id;
              return (
                <button
                  key={v.id}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActiveVariantId(v.id); }}
                  className={`w-7 h-7 rounded-md overflow-hidden border-2 shrink-0 transition-colors ${isActive ? 'border-[var(--theme-primary)]' : 'border-gray-100 hover:border-gray-300'}`}
                  title={v.label}
                >
                  {thumb ? (
                    <img src={thumb} alt={v.label} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-slate-50 flex items-center justify-center text-[10px] font-bold text-slate-400">
                      {v.label.charAt(0)}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] truncate" style={{ color: accent }}>
            {cat ? `${cat.name} › ` : ''}{sub ? sub.name : '—'}
          </p>
          <p className="text-[13px] font-extrabold text-[#0d0d0d] truncate">{p.name}</p>
          {p.description && <p className="text-[10px] font-medium text-slate-500 line-clamp-1 mt-0.5">{p.description}</p>}
        </div>
        <div className="flex items-center justify-between gap-1 text-[10px] font-extrabold uppercase tracking-[0.05em] w-full min-h-[18px]">
          <div className="flex items-center gap-1.5 truncate">
            <span style={{ color: accent }}>GST {gst ? `${gst.rate}%` : '0%'}</span>
            <span className="text-slate-300">•</span>
            <span className="text-slate-500 truncate">{variantCount || 1} variant{(variantCount || 1) === 1 ? '' : 's'}</span>
          </div>
        </div>
        <footer className="flex items-center justify-end gap-2 mt-auto pt-1.5 border-t border-[var(--theme-tertiary)]">
          <button type="button" aria-label={`Edit ${p.name}`} onClick={() => onEdit(p)} className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-[var(--theme-primary)] hover:shadow-sm text-[10px] font-bold uppercase tracking-[0.1em] transition-all">
            <Pen size={12} /> Edit
          </button>
          <button type="button" aria-label={`Remove ${p.name}`} onClick={() => onRemove(p)} className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-transparent border border-transparent text-slate-500 hover:bg-white hover:border-slate-300 hover:text-rose-600 hover:shadow-sm text-[10px] font-bold uppercase tracking-[0.1em] transition-all">
            <Trash2 size={12} /> Remove
          </button>
        </footer>
      </div>
    </motion.article>
  );
};

ProductCard.propTypes = {
  org: PropTypes.object,
  stock: PropTypes.number,
  inventoryRows: PropTypes.array,
  p: PropTypes.object.isRequired,
  sub: PropTypes.object,
  cat: PropTypes.object,
  gst: PropTypes.object,
  accent: PropTypes.string.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export const ProductsTab = ({ org, showNotification }) => {
  const { version: storeVersion } = useSharedStore();
  const [localVersion, setLocalVersion] = useState(0);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [draft, setDraft] = useState(blank(org.id));
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const subCats = useMemo(() => pSubCategoriesOf(org.id), [org.id, storeVersion]);
  const cats = useMemo(() => pCategoriesOf(org.id), [org.id, storeVersion]);
  const gstList = useMemo(() => gstRatesOf(org.id), [org.id, storeVersion]);
  const inventoryRows = useMemo(() => stockRowsOf(org.id, null, 'active'), [org.id, storeVersion]);

  const list = useMemo(() => {
    const all = catalogProductsOf(org.id);
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter((p) => p.name.toLowerCase().includes(q));
  }, [org.id, search, localVersion, storeVersion]);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const totalPages = Math.max(1, Math.ceil(list.length / perPage));
  const start = (page - 1) * perPage;
  const pageList = list.slice(start, start + perPage);

  const accent = org.theme.primary;

  const openCreate = () => { setDraft(blank(org.id)); setMode('create'); setModalOpen(true); };
  const openEdit = (p) => {
    const draftData = JSON.parse(JSON.stringify(p));
    if (!draftData.images) draftData.images = [];
    if (draftData.image && draftData.images.length === 0) draftData.images = [draftData.image];
    if (draftData.shelfLifeDays === undefined || draftData.shelfLifeDays === null) draftData.shelfLifeDays = '';
    // Fall back to inferring the image mode for products saved before it existed.
    if (!draftData.imageMode) {
      const hasVariantImgs = (draftData.variants || []).some((v) => (v.images || []).length > 0);
      const hasCommonImgs = draftData.images.length > 0;
      draftData.imageMode = hasVariantImgs ? (hasCommonImgs ? 'both' : 'variant') : 'common';
    }
    // Sync actual stock from inventory tracking so edit modal displays real current stock
    const pRows = stockRowsOf(org.id, null, null).filter(r => r.productId === p.id);
    const isMultiVar = Boolean(draftData.variantFields && draftData.variantFields.length > 0 && draftData.variants && draftData.variants.length > 0);
    if (!isMultiVar) {
      const row = pRows[0];
      const realQty = row ? (Number(row.qty) || 0) : (Number(draftData.stockQty) || 0);
      draftData.stockQty = realQty;
      if (draftData.variants && draftData.variants.length > 0) {
        draftData.variants = draftData.variants.map(v => ({ ...v, stockQty: realQty }));
      }
    } else {
      draftData.variants = draftData.variants.map(v => {
        const row = pRows.find(r => r.variantId === v.id || (v.id === 'base' && !r.variantId));
        return { ...v, stockQty: row ? (Number(row.qty) || 0) : (Number(v.stockQty) || 0) };
      });
      draftData.stockQty = draftData.variants.reduce((sum, v) => sum + (Number(v.stockQty) || 0), 0);
    }
    setDraft(draftData);
    setMode('edit');
    setModalOpen(true);
  };

  const submit = async (record) => {
    const final = { ...record, orgId: org.id };
    final.basePrice = Number(final.basePrice) || 0;
    final.stockQty = Number(final.stockQty) || 0;
    final.shelfLifeDays = Number(final.shelfLifeDays) || 0;
    const isMultiVar = Boolean(final.variantFields && final.variantFields.length > 0 && final.variants && final.variants.length > 0);
    if (!isMultiVar) {
      if (final.variants && final.variants.length > 0) {
        final.variants = final.variants.map(v => ({ ...v, price: final.basePrice, stockQty: final.stockQty }));
      }
    } else {
      final.variants = final.variants.map(v => ({ ...v, stockQty: Number(v.stockQty) || 0 }));
      final.stockQty = final.variants.reduce((sum, v) => sum + v.stockQty, 0);
    }
    if (!final.gstId) final.gstId = null;
    if (mode === 'create') {
      const newId = await addCatalogProduct(final);
      final.id = newId;
      const hasVars = final.variants && final.variants.length > 0;
      if (final.openingStockLocationId) {
        if (!hasVars && final.openingStockQty > 0) {
          await addStockRow({
            orgId: org.id,
            locationId: final.openingStockLocationId,
            productId: final.id,
            variantId: final.variants && final.variants.length > 0 ? final.variants[0].id : '',
            qty: Number(final.openingStockQty),
            costPrice: 0,
            sellPrice: final.basePrice,
            status: 'active',
            note: 'Opening Stock'
          });
        } else if (hasVars) {
          for (const v of final.variants) {
            if (v.openingStockQty > 0) {
              await addStockRow({
                orgId: org.id,
                locationId: final.openingStockLocationId,
                productId: final.id,
                variantId: v.id,
                qty: Number(v.openingStockQty),
                costPrice: 0,
                sellPrice: v.price,
                status: 'active',
                note: 'Opening Stock'
              });
            }
          }
        }
      }
    } else {
      await updateCatalogProduct(record.id, final);
    }
    if (showNotification) showNotification(`${mode === 'create' ? 'Created' : 'Updated'} ${final.name} · ${final.variants.length || 1} variant${(final.variants.length || 1) === 1 ? '' : 's'}`, 'success');
    setModalOpen(false);
    setLocalVersion((v) => v + 1);
  };

  const doDelete = () => {
    if (!confirmDelete) return;
    deleteCatalogProduct(confirmDelete.id);
    if (showNotification) showNotification(`Removed ${confirmDelete.name}`, 'success');
    setConfirmDelete(null);
    setLocalVersion((v) => v + 1);
  };

  if (modalOpen) {
    return (
      <div className="flex flex-col h-full w-full">
        <ProductForm
          org={org}
          open={modalOpen}
          draft={draft}
          setDraft={setDraft}
          onClose={() => setModalOpen(false)}
          onSubmit={submit}
          mode={mode}
          subCats={subCats}
          cats={cats}
          gstList={gstList}
          accent={accent}
          showNotification={showNotification}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 min-w-0 items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[180px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…"
              className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none transition-colors" />
          </div>
          <ViewToggle value={viewMode} onChange={setViewMode} />
        </div>
        <button type="button" onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] shadow-lg shrink-0"
          style={{ background: accent, boxShadow: `0 12px 24px -12px ${accent}` }}>
          <Plus size={14} aria-hidden="true" /> New product
        </button>
      </section>

      {viewMode === 'grid' ? (
        <section className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
          {pageList.map((p) => {
            const sub = findPSubCategory(p.subCategoryId);
            const cat = sub ? findPCategory(sub.categoryId) : null;
            const gst = findGst(p.gstId);
            const pRows = inventoryRows.filter(r => r.productId === p.id);
            const fallbackQty = (p.variants && p.variants.length > 0)
              ? p.variants.reduce((sum, v) => sum + (Number(v.stockQty) || 0), 0)
              : (Number(p.stockQty) || 0);
            const stock = pRows.length > 0 ? pRows.reduce((sum, r) => sum + (Number(r.qty) || 0), 0) : fallbackQty;
            return (
              <ProductCard 
                key={p.id} 
                org={org}
                p={p} 
                cat={cat} 
                sub={sub} 
                gst={gst} 
                stock={stock}
                inventoryRows={inventoryRows}
                accent={accent} 
                onEdit={openEdit} 
                onRemove={setConfirmDelete} 
              />
            );
          })}
          {list.length === 0 && (
            <div className="col-span-full py-16 bg-white border border-dashed border-[var(--theme-tertiary)] rounded-lg text-center">
              <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">No products yet.</p>
              <button type="button" onClick={openCreate} className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]" style={{ background: accent }}>
                <Plus size={12} /> Create the first one
              </button>
            </div>
          )}
        </section>
      ) : (
        <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden shadow-sm">
          <div className="overflow-x-auto scrollbar-hide">
            <table className="w-full text-left">
              <thead className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-4 py-3 whitespace-nowrap w-24">Product Image</th>
                  <th className="px-4 py-3 whitespace-nowrap">Product Name</th>
                  <th className="px-4 py-3 whitespace-nowrap">Category</th>
                  <th className="px-4 py-3 whitespace-nowrap">Sub-Category</th>
                  <th className="px-4 py-3 whitespace-nowrap">Price</th>
                  <th className="px-4 py-3 whitespace-nowrap text-center">GST</th>
                  <th className="px-4 py-3 whitespace-nowrap text-center">Variants</th>
                  <th className="px-4 py-3 whitespace-nowrap text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageList.map((p) => {
                  const sub = findPSubCategory(p.subCategoryId);
                  const cat = sub ? findPCategory(sub.categoryId) : null;
                  const gst = findGst(p.gstId);
                  const pRows = inventoryRows.filter(r => r.productId === p.id);
                  const fallbackQty = (p.variants && p.variants.length > 0)
                    ? p.variants.reduce((sum, v) => sum + (Number(v.stockQty) || 0), 0)
                    : (Number(p.stockQty) || 0);
                  const stock = pRows.length > 0 ? pRows.reduce((sum, r) => sum + (Number(r.qty) || 0), 0) : fallbackQty;
                  
                  const imageMode = p.imageMode || 'common';
                  let cover = null;
                  if (imageMode === 'common' || imageMode === 'both') {
                    cover = (p.images && p.images.length > 0) ? p.images[0] : null;
                  }
                  if (!cover && p.variants && p.variants.length > 0) {
                    for (const v of p.variants) {
                      if (v.images && v.images.length > 0) {
                        cover = v.images[0];
                        break;
                      }
                    }
                  }

                  const variantCount = p.variants?.length || 0;

                  return (
                    <motion.tr
                      key={p.id}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="border-b border-[var(--theme-tertiary)] last:border-0 transition-colors hover:bg-slate-50 group"
                    >
                      <td className="px-4 py-2 align-middle">
                        <div className="w-10 h-10 rounded-md overflow-hidden bg-[var(--theme-secondary)] flex items-center justify-center shrink-0 border border-slate-100">
                          {cover ? (
                            <img src={cover} alt={p.name} className="w-full h-full object-cover" />
                          ) : (
                            <ImageIcon size={16} className="text-slate-300" aria-hidden="true" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <p className="text-[13px] font-extrabold text-[#0d0d0d] whitespace-nowrap">{p.name}</p>
                        {p.description && <p className="text-[10px] font-medium text-slate-500 truncate max-w-[150px]">{p.description}</p>}
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <p className="text-[11px] font-bold text-slate-700 truncate">{cat ? cat.name : '—'}</p>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <p className="text-[11px] font-bold truncate" style={{ color: accent }}>{sub ? sub.name : '—'}</p>
                      </td>
                      <td className="px-4 py-3 text-[12px] font-bold text-slate-700 whitespace-nowrap align-middle">
                        {(() => {
                          if (p.variants && p.variants.length > 0) {
                            const prices = p.variants.map(v => Number(v.price) || 0);
                            const min = Math.min(...prices);
                            const max = Math.max(...prices);
                            return min === max ? `₹${min}` : `₹${min} - ₹${max}`;
                          }
                          return `₹${p.basePrice || 0}`;
                        })()}
                      </td>
                      <td className="px-4 py-3 text-[11px] font-bold text-slate-600 whitespace-nowrap text-center align-middle">
                        {gst ? `${gst.rate}%` : '0%'}
                      </td>
                      <td className="px-4 py-3 text-[11px] font-bold text-slate-600 whitespace-nowrap text-center align-middle">
                        {variantCount || 1}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap align-middle">
                        <RowActions>
                          <IconAction icon={Pen} label={`Edit ${p.name}`} title="Edit" onClick={() => openEdit(p)} />
                          <IconAction icon={Trash2} tone="danger" label={`Remove ${p.name}`} title="Remove" onClick={() => setConfirmDelete(p)} />
                        </RowActions>
                      </td>
                    </motion.tr>
                  );
                })}
                {list.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-5 py-16 text-center">
                      <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">No products yet.</p>
                      <button type="button" onClick={openCreate} className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em]" style={{ background: accent }}>
                        <Plus size={12} /> Create the first one
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {list.length > 0 && (
        <section className="flex items-center justify-between mt-auto border-t border-[var(--theme-tertiary)] shrink-0 px-5 py-2">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Showing</span>
            <CustomSelect
              value={perPage}
              onChange={(val) => {
                setPerPage(Number(val));
                setPage(1);
              }}
              options={[
                { value: 10, label: '10' },
                { value: 25, label: '25' },
                { value: 50, label: '50' },
                { value: 100, label: '100' },
                { value: list.length > 0 ? list.length : 200, label: 'All' },
              ]}
              className="w-16 bg-white border border-[var(--theme-tertiary)] rounded-sm px-2 py-1 text-[11px] font-bold text-slate-700 outline-none hover:border-[var(--theme-primary)] transition-colors cursor-pointer"
              menuUp={true}
            />
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 rounded-sm bg-white border border-[var(--theme-tertiary)] flex items-center justify-center disabled:opacity-50 hover:border-[var(--theme-primary)] transition-colors">
              <ChevronLeft size={14} />
            </button>
            <span className="px-3 py-1 rounded-sm bg-[#0d0d0d] font-bold tracking-widest text-[11px] text-white">{page} / {totalPages}</span>
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 rounded-sm bg-white border border-[var(--theme-tertiary)] flex items-center justify-center disabled:opacity-50 hover:border-[var(--theme-primary)] transition-colors">
              <ChevronRight size={14} />
            </button>
          </div>
        </section>
      )}



      {confirmDelete && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(6px)' }}>
          <div className="w-full max-w-sm bg-white rounded-lg shadow-xl border border-rose-100 overflow-hidden">
            <div className="px-6 py-6 text-center">
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center text-rose-500 mb-3" style={{ background: 'rgba(244,63,94,0.10)' }}><Trash2 size={20} /></div>
              <h4 className="text-base font-extrabold text-[#0d0d0d]">Remove {confirmDelete.name}?</h4>
              <p className="mt-1 text-[11px] font-medium text-slate-500">All its variant rows will be removed from the catalog.</p>
            </div>
            <div className="flex border-t border-rose-100">
              <button type="button" onClick={() => setConfirmDelete(null)} className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={doDelete} className="flex-1 py-3 text-xs font-semibold uppercase tracking-[0.14em] bg-rose-500 text-white hover:bg-rose-600">Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ProductsTab.propTypes = {
  org: PropTypes.object.isRequired,
  showNotification: PropTypes.func,
};
